/**
 * BrandDetail — Pagina de detalle de una marca.
 * Ruta: /v2/brands/:id
 *
 * Rediseno visual aplicado 2026-05-27.
 * Logica intacta: fetch, OAuth Meta, AccountSelectorModal, disconnect, etc.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useWorkspacesV2 } from '@/contexts/WorkspacesV2Context';
import { brandsApi, connectionsApi } from '@/components/v2/api';
import ConnectMetaButton from '@/components/v2/ConnectMetaButton';
import AccountSelectorModal from '@/components/v2/AccountSelectorModal';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft, Building2, Globe, Briefcase, Calendar,
  Plus, Unlink, AlertCircle, CheckCircle2, ExternalLink,
  Facebook, Search, BarChart3, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import './BrandDetail.css';


const PROVIDERS = [
  {
    key: 'meta',
    name: 'Meta Ads',
    description: 'Facebook & Instagram Ads, Pages, IG Business',
    services: ['meta_ads', 'facebook_page', 'instagram_business'],
    icon: Facebook,
    iconBoxClass: 'meta',
    enabled: true,
  },
  {
    key: 'google_ads',
    name: 'Google Ads',
    description: 'Campañas de Search, Display, YouTube',
    services: ['google_ads'],
    icon: BarChart3,
    iconBoxClass: 'google',
    enabled: false,
  },
  {
    key: 'ga4',
    name: 'Google Analytics 4',
    description: 'Métricas web (sesiones, conversiones, etc.)',
    services: ['ga4'],
    icon: BarChart3,
    iconBoxClass: 'analytics',
    enabled: false,
  },
  {
    key: 'gsc',
    name: 'Search Console',
    description: 'SEO orgánico y rendimiento en Google',
    services: ['search_console'],
    icon: Search,
    iconBoxClass: 'console',
    enabled: false,
  },
];

const SERVICE_LABELS = {
  meta_ads: 'Ad Account',
  facebook_page: 'Página',
  instagram_business: 'Instagram',
  google_ads: 'Cuenta Google Ads',
  ga4: 'GA4 Property',
  search_console: 'Sitio GSC',
};

const PROVIDER_OF_SERVICE = {
  meta_ads: 'meta',
  facebook_page: 'meta',
  instagram_business: 'meta',
  google_ads: 'google_ads',
  ga4: 'ga4',
  search_console: 'gsc',
};


export default function BrandDetail() {
  const { id: brandId } = useParams();
  const navigate = useNavigate();
  const { activeWorkspace, isAdmin } = useWorkspacesV2();

  const [brand, setBrand] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorIdentityId, setSelectorIdentityId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, conns] = await Promise.all([
        brandsApi.get(brandId),
        connectionsApi.list(brandId, { onlyActive: true }).catch(() => []),
      ]);
      setBrand(b);
      setConnections(conns || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    if (brandId) loadAll();
  }, [brandId, loadAll]);

  const handleConnect = (providerKey) => {
    if (providerKey === 'meta') {
      toast.info('Próximo paso: implementar ConnectMetaButton');
    } else {
      toast.info(`OAuth ${providerKey} pendiente`);
    }
  };

  const handleDisconnect = async (conn) => {
    if (!window.confirm(`¿Desconectar "${conn.external_account_name || conn.external_account_id}"?`)) return;
    try {
      await connectionsApi.disconnect(conn.id);
      toast.success('Conexión desconectada');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al desconectar');
    }
  };

  // Agrupar conexiones por provider
  const connectionsByProvider = connections.reduce((acc, conn) => {
    const provKey = PROVIDER_OF_SERVICE[conn.service];
    if (!provKey) return acc;
    if (!acc[provKey]) acc[provKey] = [];
    acc[provKey].push(conn);
    return acc;
  }, {});

  // ============ RENDER ============

  if (loading) {
    return (
      <DashboardLayout>
        <div className="brand-detail-wrapper">
          <div className="brand-detail-container">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="brand-detail-wrapper">
          <div className="brand-detail-container">
            <div style={{padding: '2rem', textAlign: 'center', color: '#ef4444'}}>
              <AlertCircle style={{width: 32, height: 32, margin: '0 auto 1rem'}} />
              <p>Error: {error}</p>
              <button onClick={() => navigate('/v2/brands')} className="btn-connect btn-connect-primary" style={{marginTop: '1rem', maxWidth: 200}}>
                Volver a marcas
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!brand) return null;

  return (
    <DashboardLayout>
      <div className="brand-detail-wrapper">
        <div className="brand-detail-container">

          {/* 1. Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/v2/brands" className="breadcrumb-link">
              <ArrowLeft style={{width: 14, height: 14, display: 'inline', marginRight: 4, verticalAlign: 'middle'}} />
              Marcas
            </Link>
            <span className="breadcrumb-separator">·</span>
            <span className="breadcrumb-current">{brand.name}</span>
          </div>

          {/* 2. Header de la marca */}
          <div className="brand-header-card">
            <div className="brand-header-left">
              <div className="brand-avatar-box">
                <Building2 style={{width: 24, height: 24}} />
              </div>
              <div className="brand-header-info">
                <h1 className="brand-name">{brand.name}</h1>
                <div className="brand-meta">
                  {brand.industry && (
                    <span className="brand-meta-item">
                      <Briefcase style={{width: 14, height: 14}} />
                      {brand.industry}
                    </span>
                  )}
                  {brand.website && (
                    
                    <a href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`}
                      target="_blank"
                      rel="noreferrer"
                      className="brand-meta-link"
                    >
                      <Globe style={{width: 14, height: 14}} />
                      {brand.website}
                      <ExternalLink style={{width: 12, height: 12}} />
                    </a>
                  )}
                  <span className="brand-meta-item">
                    <Calendar style={{width: 14, height: 14}} />
                    Creada: {new Date(brand.created_at).toLocaleDateString('es-CL')}
                  </span>
                </div>
              </div>
            </div>
            <div>
              {brand.legacy_client_id ? (
                <span className="badge-migrated">Migrada de v1</span>
              ) : (
                <span className="badge-migrated">Nueva v2</span>
              )}
            </div>
          </div>

          {brand.notes && (
            <div className="brand-notes" style={{marginTop: '-0.5rem'}}>
              <FileText style={{width: 14, height: 14, marginTop: 2, flexShrink: 0}} />
              <span>{brand.notes}</span>
            </div>
          )}

          {/* 3. Conexiones */}
          <div className="connections-section">
            <div className="connections-title-box">
              <h2 className="connections-title">Conexiones</h2>
              <span className="connections-count">
                {connections.length === 0
                  ? 'Conecta plataformas para empezar'
                  : `${connections.length} ${connections.length === 1 ? 'conexión activa' : 'conexiones activas'}`}
              </span>
            </div>

            <div className="connections-grid">
              {PROVIDERS.map(provider => {
                const Icon = provider.icon;
                const provConns = connectionsByProvider[provider.key] || [];
                const isConnected = provConns.length > 0;

                return (
                  <div key={provider.key} className="platform-card">
                    {/* Header */}
                    <div className="platform-header">
                      <div className="platform-title-group">
                        <div className={`platform-icon-box ${provider.iconBoxClass}`}>
                          <Icon style={{width: 18, height: 18}} />
                        </div>
                        <div>
                          <div className="platform-name">{provider.name}</div>
                          <div className="platform-description">{provider.description}</div>
                        </div>
                      </div>
                      {isConnected && (
                        <span className="connected-badge">
                          <CheckCircle2 style={{width: 12, height: 12}} />
                          Conectado
                        </span>
                      )}
                    </div>

                    {/* Lista de conexiones activas */}
                    {isConnected && (
                      <div className="connections-list">
                        {provConns.map(conn => (
                          <div key={conn.id} className="connection-item">
                            <div className="connection-item-content">
                              <div className="connection-item-row">
                                <span className="connection-service-tag">
                                  {SERVICE_LABELS[conn.service] || conn.service}
                                </span>
                                <span className="connection-name">
                                  {conn.external_account_name || conn.external_account_id}
                                </span>
                              </div>
                              <div className="connection-id">
                                ID: {conn.external_account_id}
                              </div>
                            </div>
                            {isAdmin && (
                              <button
                                className="btn-disconnect-icon"
                                onClick={() => handleDisconnect(conn)}
                                title="Desconectar"
                              >
                                <Unlink style={{width: 14, height: 14}} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Botón conectar */}
                    {isAdmin && (
                      <div>
                        {provider.enabled ? (
                          provider.key === 'meta' ? (
                            <ConnectMetaButton
                              workspaceId={activeWorkspace?.id}
                              variant={isConnected ? 'outline' : 'default'}
                              className={`btn-connect ${isConnected ? 'btn-connect-outline' : 'btn-connect-primary'}`}
                              onSuccess={(identityId) => {
                                setSelectorIdentityId(identityId);
                                setSelectorOpen(true);
                              }}
                            >
                              <Plus style={{width: 14, height: 14}} />
                              {isConnected ? 'Conectar otra cuenta' : 'Conectar Meta Ads'}
                            </ConnectMetaButton>
                          ) : (
                            <button
                              className={`btn-connect ${isConnected ? 'btn-connect-outline' : 'btn-connect-primary'}`}
                              onClick={() => handleConnect(provider.key)}
                            >
                              <Plus style={{width: 14, height: 14}} />
                              {isConnected ? 'Conectar otra cuenta' : `Conectar ${provider.name}`}
                            </button>
                          )
                        ) : (
                          <button className="btn-connect btn-coming-soon" disabled>
                            Próximamente
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 4. Debug info */}
          <div className="debug-info-section">
            <div className="debug-header" onClick={() => setShowDebug(!showDebug)}>
              <span className={`debug-arrow ${showDebug ? 'expanded' : ''}`}>▼</span>
              <h3>Debug info</h3>
            </div>
            {showDebug && (
              <div className="debug-body">
                <pre className="debug-json">
                  {JSON.stringify({ brand, connections }, null, 2)}
                </pre>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Modal de seleccion de cuentas Meta post-OAuth */}
      <AccountSelectorModal
        open={selectorOpen}
        onOpenChange={setSelectorOpen}
        identityId={selectorIdentityId}
        brandId={brandId}
        existingConnections={connections}
        onSuccess={({ created }) => {
          if (created > 0) loadAll();
        }}
      />
    </DashboardLayout>
  );
}
